<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { TelInput, normalizedCountries, isSelected, clickOutsideAction } from "svelte-tel-input";
  import 'svelte-tel-input/styles/flags.css';
  import type {IUser} from "$lib/interfaces/IUserMe";

  let userBasedCountryCode: string | null = user.countryCode;


  let searchText = $state('');
  let isOpen = $state(false);
  interface Props {
    user: IUser;
    clickOutside?: boolean;
    closeOnClick?: boolean;
    disabled?: boolean;
    detailedValue?: any;
    value?: string;
    searchPlaceholder?: string;
    name?: string;
    selectedCountry?: any;
    valid?: boolean;
    options?: any;
  }

  let {
    user,
    clickOutside = true,
    closeOnClick = true,
    disabled = false,
    detailedValue = $bindable(null),
    value = $bindable('+442071838750'),
    searchPlaceholder = 'Search',
    name = 'phone',
    selectedCountry = $bindable(userBasedCountryCode),
    valid = $bindable(true),
    options = { invalidateOnCountryChange: true }
  }: Props = $props();

  let countryDropdownInput: HTMLInputElement = $state()

  let selectedCountryDialCode =
    $derived(normalizedCountries.find((el) => el.iso2 === selectedCountry)?.dialCode || null);

  const toggleDropDown = (e: any) => {
    e?.preventDefault();
    if (disabled) return;
    isOpen = !isOpen;
    // countryDropdownInput.autofocus = true;
  };

  const closeDropdown = (e: any = null) => {
    if (isOpen) {
      e?.preventDefault();
      isOpen = false;
      searchText = '';
    }
  };

  const selectClick = () => {
    if (closeOnClick) closeDropdown();
  };

  const closeOnClickOutside = () => {
    if (clickOutside) {
      closeDropdown();
    }
  };

  const sortCountries = (countries: any, text: any) => {
    const normalizedText = text.trim().toLowerCase();
    if (!normalizedText) {
      return countries.sort((a: any, b: any) => a.label.localeCompare(b.label));
    }
    return countries.sort((a: any, b: any) => {
      const aNameLower = a.name.toLowerCase();
      const bNameLower = b.name.toLowerCase();
      const aStartsWith = aNameLower.startsWith(normalizedText);
      const bStartsWith = bNameLower.startsWith(normalizedText);
      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;
      const aIndex = aNameLower.indexOf(normalizedText);
      const bIndex = bNameLower.indexOf(normalizedText);
      if (aIndex === -1 && bIndex === -1) return a.id.localeCompare(b.id);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      const aWeight = aIndex + (aStartsWith ? 0 : 1);
      const bWeight = bIndex + (bStartsWith ? 0 : 1);
      return aWeight - bWeight;
    });
  };

  const handleSelect = (val: any, e?: any) => {
    if (disabled) return;
    e?.preventDefault();
    if (
      selectedCountry === undefined ||
      selectedCountry === null ||
      (typeof selectedCountry === typeof val && selectedCountry !== val)
    ) {
      selectedCountry = val;
      onChange(val);
      selectClick();
    } else {
      dispatch('same', { option: val });
      selectClick();
    }
  };

  const dispatch = createEventDispatcher();

  const onChange = (selectedCountry: any) => {
    dispatch('change', { option: selectedCountry });
  };
</script>

<div
  class="flex relative rounded-full w-full {valid
		? ``
		: ` ring-red-500 dark:ring-red-500 ring-1 focus-within:ring-offset-1 focus-within:ring-offset-red-500/50 focus-within:ring-1`}"
>
  <div class="flex" use:clickOutsideAction={closeOnClickOutside}>
    <button
      id="states-button"
      data-dropdown-toggle="dropdown-states"
      class="relative flex-shrink-0 overflow-hidden z-10 whitespace-nowrap inline-flex items-center py-2.5 px-4 text-sm font-medium text-center text-white bg-primary-dark rounded-l-full hover:bg-primary-lightBlue"
      type="button"
      role="combobox"
      aria-controls="dropdown-countries"
      aria-expanded="false"
      aria-haspopup="false"
      onclick={toggleDropDown}
    >
      {#if selectedCountry && selectedCountry !== null}
        <div class="inline-flex items-center text-left">
          <img class="w-6 rounded mr-1" src="https://flagcdn.com/{selectedCountry.toLowerCase()}.svg" alt="">
<!--          <span class="flag flag-{selectedCountry.toLowerCase()} flex-shrink-0 mr-3" />-->
          <span class="text-white">+{selectedCountryDialCode}</span>
        </div>
      {:else}
        Please select
      {/if}
      <svg
        aria-hidden="true"
        class="ml-1 w-4 h-4 {isOpen ? 'rotate-180' : ''}"
        fill="currentColor"
        viewBox="0 0 20 20"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
          clip-rule="evenodd"
        />
      </svg>
    </button>
    {#if isOpen}
      <div
        id="dropdown-countries"
        class="absolute z-10 max-w-fit rounded-xl divide-y divide-gray-100 bg-primary-dark overflow-hidden shadow-lg translate-y-11"
        data-popper-reference-hidden=""
        data-popper-escaped=""
        data-popper-placement="bottom"
        aria-orientation="vertical"
        aria-labelledby="country-button"
        tabindex="-1"
      >
        <div
          class="text-sm max-h-64 overflow-y-auto"
          aria-labelledby="countries-button"
          role="listbox"
        >
          <input
            aria-autocomplete="list"
            type="text"
            class="px-4 py-2 text-white placeholder-white border-b border-primary-lightBlue focus:outline-none w-full sticky top-0 bg-primary-dark"
            bind:value={searchText}
            placeholder={searchPlaceholder}
            bind:this={countryDropdownInput}
            autofocus
          />
          {#each sortCountries(normalizedCountries, searchText) as country (country.id)}
            {@const isActive = isSelected(country.iso2, selectedCountry)}
            <div id="country-{country.iso2}" role="option" aria-selected={isActive}>
              <button
                value={country.iso2}
                type="button"
                class="inline-flex py-2 px-4 w-full text-sm hover:bg-primary-lightBlue active:bg-primary-blue overflow-hidden
                            {isActive
									? 'bg-primary-dark text-white'
									: 'hover:text-white dark:text-gray-400'}"
                onclick={(e) => {
									handleSelect(country.iso2, e);
								}}
              >
                <div class="inline-flex items-center text-left">
                  <img class="w-6 rounded-sm mr-2" src="https://flagcdn.com/{country.iso2.toLowerCase()}.svg" alt="">
                  <span class="mr-2">{country.name}</span>
                  <span class="text-gray-200">+{country.dialCode}</span>
                </div>
              </button>
            </div>
          {/each}
        </div>
      </div>
    {/if}
  </div>

  <TelInput
    id="tel-input"
    bind:country={selectedCountry}
    bind:detailedValue
    bind:value
    bind:valid
    {options}
    required={true}
    {name}
    class="text-sm rounded-r-full block w-full p-2.5 bg-primary-dark text-white outline-0 ml-4"
  />
</div>
